import { IRepository } from './repository.interface';
import { AppDatabase } from '../app.database';
import Dexie, { IndexableType } from 'dexie';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

type Params = {
	[key: string]: any
}

export abstract class BaseRepository<TEntity, TKey extends IndexableType, TDto> implements IRepository<TEntity, TKey> {

	constructor(protected db: AppDatabase, private collectionName: string) { }

	/**
	 * The table in the active local store. Read it one time for each call and use that one
	 * reference for the transaction and the query so that both always go to the same database.
	 */
	private get table(): Dexie.Table<TEntity, TKey> {
		return this.db.table<TEntity, TKey>(this.collectionName);
	}

	public add(value: TEntity): Observable<void> {
		const table = this.table;
		return from(table.db.transaction('rw', table, async () => {
			await table.put(value);
		}));
	}

	public bulkAdd(values: TEntity[], key: string) {
		const table = this.table;
		return from(table.db.transaction('rw', table, async () => {
			await table.bulkAdd(values, [key]);
		}));
	}

	public update(value: TEntity, key: TKey): Observable<void> {
		const table = this.table;
		return from(table.db.transaction('rw', table, async () => {
			await table.update(key, { value });
		}));
	}

	public put(value: TEntity, key?: TKey): Observable<void> {
		const table = this.table;
		return from(table.db.transaction('rw', table, async () => {
			await table.put(value, key);
		}));
	}

	public bulkPut(values: TEntity[], key: string) {
		const table = this.table;
		return from(table.db.transaction('rw', table, async () => {
			await table.bulkAdd(values, [key]);
		}));
	}

	public clear(): Observable<void> {
		return from(this.table.clear());
	}

	public count(): Observable<number> {
		return from(this.table.count());
	}

	public isEmpty(): Observable<boolean> {
		return from(this.count()).pipe(map(count => count > 0));
	}



	public get(key: string, value: any): Observable<TEntity> {
		const param: Params = {};
		param[key] = value;
		return from(this.table.where(param).first()) as Observable<TEntity>;
	}

	public getMultiple(key: string, value: any): Observable<TEntity[]> {
		return from(this.table.where(key).equals(value).toArray());
	}

	public getAll(): Observable<TEntity[]> {
		return from(this.table.toArray());
	}

	public getWhere(key: string, values: any[]): Observable<TEntity[]> {
		return from(this.table.where(key).anyOf(values).toArray())
	}

	public abstract convertDto(entity: TEntity): TDto;
	public abstract convertEntity(dto: TDto): TEntity;
}
